import type { AlertColor, SelectChangeEvent } from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import type { CertificateModel } from "web-pki";
import { LacunaWebPKI } from "web-pki";

import {
  signatureDigitalFinish,
  signatureDigitalGetStatus,
  signatureDigitalStart,
} from "../../../api/signer";
import config from "../../../utils/config";

interface CertificateSignatureModalProps {
  id: string;
  open: boolean;
  onClose: () => void;
  onBack: () => void;
  setMessageAlert: (string: string) => void;
  setTypeAlert: (string: AlertColor) => void;
  setOpenAlert: (boolean: boolean) => void;
  signatureType: "0" | "1" | "2" | "3";
  onSuccess?: () => void;
}

export default function CertificateSignatureModal({
  open,
  onClose,
  onBack,
  setMessageAlert,
  setOpenAlert,
  setTypeAlert,
  id,
  signatureType,
  onSuccess,
}: CertificateSignatureModalProps): React.JSX.Element {
  const params = useParams() as { hash: string };
  const [selectedCertificate, setSelectedCertificate] = useState<string>("");
  const [signatureId, setSignatureId] = useState<string>("");
  const [certificates, setCertificates] = useState<CertificateModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [isErrorWebPki, setIsErrorWebPki] = useState(false);
  const [pkiError, setPkiError] = useState<string | null>(null);
  const [errorField, setErrorField] = useState("");
  const pki = useMemo(() => new LacunaWebPKI(config.LacunaLicense), []);

  const onWebPkiReady = useCallback(() => {
    refreshCertificates();
  }, []);

  const refreshCertificates = (): void => {
    pki
      .listCertificates()
      .success((certs) => {
        setCertificates(certs);
        if (certs.length > 0) {
          setSelectedCertificate(certs[0].thumbprint);
        }
        setLoading(false);
      })
      .fail(() => {
        setPkiError(
          "Ocorreu um erro ao listar os certificados. Verifique se o Web PKI está instalado e em execução.",
        );
        setLoading(false);
      });
  };

  useEffect(() => {
    if (open) {
      setLoading(true);
      setPkiError(null);

      pki.init({
        ready: onWebPkiReady,
        notInstalled(status: number) {
          if (status === 1) {
            setIsErrorWebPki(true);
            setPkiError(
              "O componente Web PKI não foi detectado. Por favor, verifique se ele está instalado e em execução.",
            );
          } else if (status === 2) {
            setIsErrorWebPki(true);
            setPkiError(
              "O componente Web PKI foi detectado, mas ele está desativado. Por favor, ative-o e tente novamente.",
            );
          } else if (status === 3) {
            setPkiError(
              "Browser não suportado. Por favor, utilize um dos navegadores suportados.",
            );
          }

          setLoading(false);
        },
      });
    }
  }, [onWebPkiReady, open, pki]);

  const certificateToBase64 = (cert: CertificateModel): Promise<string> => {
    return new Promise((resolve) => {
      pki
        .readCertificate(cert)
        .success((base64) => {
          resolve(base64);
        })
        .fail(() => {
          resolve("");
        });
    });
  };

  const handleClear = (): void => {
    setSelectedCertificate("");
    setSignatureId("");
    if (errorField) {
      setErrorField("");
    }
    setOpenAlert(true);
    setIsErrorWebPki(false);
    onClose();
  };

  const sleep = (): Promise<void> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 3000);
    });
  };

  const handleSignatureFinish = async (
    configId: string,
    files: FilesSignatureDigitalFinish[],
  ): Promise<void> => {
    try {
      await signatureDigitalFinish(id, configId, files);
      setTypeAlert("success");
      setMessageAlert("Assinatura realizada com sucesso!");
      setLoading(false);
      handleClear();
      onSuccess?.();
    } catch (error) {
      setPkiError("Ocorreu um erro inesperado. Por favor, tente novamente.");
      setLoading(false);
    }
  };

  const getPkiSignHashBatch = (
    files: FilesSignatureDigital[],
    configId: string,
  ): void => {
    pki
      .signHashBatch({
        certificateThumbprint: selectedCertificate,
        batch: files.map((file) => file.toSignHash),
        digestAlgorithm: files[0].digestAlgorithm,
      })
      .success((response) => {
        const payload = files.map((file, index) => ({
          transferFileId: file.transferFileId,
          signatureValue: response.signatures[index],
        }));
        void handleSignatureFinish(configId, payload);
      })
      .fail((err) => {
        setPkiError("Ocorreu um erro inesperado. Por favor, tente novamente.");
        setLoading(false);
        throw new Error(err.message);
      });
  };

  const handleVerifySignature = async (
    configId: string,
    retries = 10,
  ): Promise<void> => {
    if (retries === 0) {
      setPkiError(
        "Ocorreu um erro ao verificar a assinatura. Por favor, tente novamente.",
      );
      setLoading(false);
      return;
    }

    try {
      const { data } = await signatureDigitalGetStatus(id, configId);
      if (data.status === "WAITING_SIGNATURE") {
        if (data.files.length > 0) {
          getPkiSignHashBatch(data.files, configId);
        } else {
          setPkiError("Nenhum arquivo para assinar foi encontrado.");
          setLoading(false);
        }
      } else {
        await sleep();
        void handleVerifySignature(configId, retries - 1);
      }
    } catch (error) {
      setPkiError("Ocorreu um erro inesperado. Por favor, tente novamente.");
      setLoading(false);
    }
  };

  const handleConfirm = async (): Promise<void> => {
    try {
      setLoading(true);
      setPkiError(null);

      if (selectedCertificate) {
        const selectedCert = certificates.find(
          (c) => c.thumbprint === selectedCertificate,
        );
        if (!selectedCert) {
          setPkiError("Certificado digital não encontrado.");
          setLoading(false);
          return;
        }
        const certBase64 = await certificateToBase64(selectedCert);
        const { data } = await signatureDigitalStart(
          id,
          certBase64,
          params.hash,
        );
        setSignatureId(data.id);
        await sleep();
        void handleVerifySignature(data.id);
      }
    } catch (error) {
      if (signatureId) {
        void handleVerifySignature(signatureId);
        return;
      }
      setPkiError("Ocorreu um erro inesperado. Por favor, tente novamente.");
      setLoading(false);
    }
  };

  const handleChange = (event: SelectChangeEvent): void => {
    setSelectedCertificate(event.target.value);
  };

  const renderContent = (): JSX.Element => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: 3,
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2, fontWeight: "400", fontSize: 14 }}>
            Carregando certificados digitais...
          </Typography>
        </Box>
      );
    }

    if (pkiError) {
      return (
        <Typography
          color="error"
          sx={{ fontWeight: "400", fontSize: 14, marginTop: 3 }}
        >
          {pkiError}
        </Typography>
      );
    }

    if (certificates.length === 0) {
      return (
        <Typography
          color="textSecondary"
          sx={{ fontWeight: "400", fontSize: 14, marginTop: 3 }}
        >
          Nenhum certificado digital válido encontrado no computador.
        </Typography>
      );
    }

    return (
      <FormControl
        fullWidth
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Select
          displayEmpty
          inputProps={{ "aria-label": "Without label" }}
          onChange={handleChange}
          value={selectedCertificate}
        >
          {certificates.map((cert) => (
            <MenuItem key={cert.thumbprint} value={cert.thumbprint}>
              {cert.subjectName} (emitido por {cert.issuerName})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <DialogTitle sx={{ fontWeight: "500", fontSize: 24, pb: 0 }}>
        Confirmar assinatura
      </DialogTitle>
      <DialogContent>{renderContent()}</DialogContent>
      <DialogActions
        sx={{ padding: "16px 24px", flexDirection: "column", gap: 2 }}
      >
        <Box
          sx={{ width: "100%", display: "flex", flexDirection: "row", gap: 2 }}
        >
          <Button
            color="primary"
            onClick={() => {
              !isErrorWebPki && onWebPkiReady();
              isErrorWebPki && pki.redirectToInstallPage();
            }}
            sx={{ mr: 1, borderRadius: "12px", width: "100%" }}
            variant="outlined"
          >
            {isErrorWebPki
              ? "Instalar Web PKI"
              : "Atualizar lista de certificados"}
          </Button>
          <Button
            color="primary"
            disabled={!selectedCertificate || loading || pkiError !== null}
            onClick={() => void handleConfirm()}
            sx={{ borderRadius: "12px", width: "100%" }}
            variant="contained"
          >
            Confirmar Assinatura
          </Button>
        </Box>
        <Button
          color="primary"
          onClick={signatureType === "1" ? onClose : onBack}
          sx={{ borderRadius: "12px", width: "100%" }}
        >
          {signatureType === "1"
            ? "Voltar aos documentos"
            : "Alterar tipo de assinatura"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
